/**
 * Shared constants for the "payment landed after an athlete was archived" note
 * (issue #826).
 *
 * When a Stripe webhook is redelivered (retries run up to ~3 days) or a stale
 * in-house invoice is paid months later, the activation finalizer refuses to
 * un-archive an athlete that staff deliberately archived after the family
 * started checkout. Instead it writes a durable note into athlete_status_history
 * so staff can decide whether to reactivate or refund.
 *
 * That ledger has hard CHECK/FK constraints (no migration was added), so the
 * note rides on an existing allow-listed reason_category ('financial_hold') and
 * carries its real meaning in reason_details, marked with the sentinel prefix
 * below. The writer (finalizeAthleteActivation.server.ts) and any reader that
 * wants to show a friendlier message (e.g. the dashboard recent-activity feed)
 * both import these constants so the two never drift.
 */

/** Existing allow-listed reason_category we reuse — see CHECK constraint on athlete_status_history.reason_category. */
export const LATE_PAYMENT_AFTER_ARCHIVE_REASON_CATEGORY = 'financial_hold';

/**
 * Sentinel that opens reason_details on these auto-written notes, so a reader
 * can tell this specific case apart from a human "financial hold" archive
 * (which shares the same reason_category). Staff-readable on its own.
 */
export const LATE_PAYMENT_AFTER_ARCHIVE_NOTE_PREFIX = 'Payment received after archive —';

/** changed_by_name used for the automatic note (plain-English author label). */
export const LATE_PAYMENT_AFTER_ARCHIVE_NOTE_AUTHOR = 'CheerVue (automatic note)';

/** Short, plain-English label for surfaces that show this note to staff. */
export const LATE_PAYMENT_AFTER_ARCHIVE_LABEL = 'payment received after archive — review';

/**
 * True when a status-history row is one of these auto-written late-payment
 * notes. Detected by reason_category + the system role + the reason_details
 * sentinel together, so a real human financial-hold archive is never mistaken
 * for it.
 */
export function isLatePaymentAfterArchiveNote(row: {
  reason_category?: string | null;
  reason_details?: string | null;
  changed_by_role?: string | null;
}): boolean {
  return (
    row.reason_category === LATE_PAYMENT_AFTER_ARCHIVE_REASON_CATEGORY &&
    (row.changed_by_role ?? '') === 'system' &&
    typeof row.reason_details === 'string' &&
    row.reason_details.startsWith(LATE_PAYMENT_AFTER_ARCHIVE_NOTE_PREFIX)
  );
}
